import React, { useContext, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Button from 'components/control/button';
import { ModalContext, IModalContext } from 'contexts/modal';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_CLICK_AREA_CARD_SAVE_SEARCH_BUTTON, GTM_CLICK_AREA_CARD_EDIT_SEARCH_BUTTON } from 'constants/events';
import styles from './style.module.scss';
import NextImage from 'next/image';
import { IUserContext, UserContext } from 'contexts/user';
import { useThemeContext } from 'contexts';
import generateSearchDescriptionFromFilters from 'utils/generate-search-description-from-filters';
import { useIsSmallMobile } from 'hooks/use-size-class';
import { buildClassName } from 'utils/build-class-name';
import { createSavedSearch } from 'data/saved-searches';
import { ThemeNames } from 'types/themes';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';

import type { ListingParams } from 'contexts/preferences/listing-params/types';
import type SavedSearch from 'data/saved-searches';

interface Props {
  listingParams: ListingParams;
  savedAttributes?: SavedSearch | undefined;
  isMobile?: boolean;
  isZoocasaTenant?: boolean;
  isCompact?: boolean;
}

const EditSearchCard = ({ savedAttributes, listingParams, isMobile, isZoocasaTenant, isCompact }: Props) => {
  const { openModal } = useContext(ModalContext) as IModalContext;
  const { t } = useTranslation(Namespaces.COMMON);

  return (
    <div className={buildClassName(styles['card-wrapper'], isMobile && styles['mobile-card-wrapper'], isCompact && styles['compact'] )}>
      <div className={styles['check-wrapper']}>
        <NextImage
          src={isZoocasaTenant ? '/next/assets/images/alert-saved-zoo.svg' : '/next/assets/images/alert-saved-exp.svg'}
          alt={t(LOCALE_COMMON_PATHS.SAVE_SEARCH_CARD_ALERT_CREATED_ALT_TEXT, {
            defaultValue: 'New area alert created',
          })}
          fill={true}
          style={{ objectFit:'contain' }}
          unoptimized
        />
      </div>
      <h2 className={styles.heading}>
        {t(LOCALE_COMMON_PATHS.SAVE_SEARCH_CARD_ALERT_CREATED_HEADING, {
          defaultValue: 'Search Alert Created',
        })}
      </h2>
      <p>
        {t(LOCALE_COMMON_PATHS.SAVE_SEARCH_CARD_ALERT_CREATED_DESCRIPTION, {
          defaultValue: 'We\'ll send you daily updates for this search',
        })}
      </p>
      <Button
        theme="secondary"
        label={t(LOCALE_COMMON_PATHS.SAVE_SEARCH_CARD_ALERT_CREATED_EDIT_BUTTON, {
          defaultValue: 'Edit Alert',
        })}
        onClick={() => {
          savedAttributes && openModal('create-saved-search', { savedAttributes, query: listingParams } );
          trackEvent(GTM_CLICK_AREA_CARD_EDIT_SEARCH_BUTTON);
        }}
      />
    </div>
  );
};

export default function SaveSearchCard({ listingParams, isCompact }: Props) {
  const { openModal } = useContext(ModalContext) as IModalContext;
  const { t } = useTranslation(Namespaces.COMMON);
  const [isSaved, setIsSaved] = useState(false);
  const [savedAttributes, setSavedAttributes] = useState<SavedSearch | undefined>(undefined);
  const [error, setError] = useState<string>();
  const { isAuthenticated } = useContext(UserContext) as IUserContext;
  const defaultSearchDescription = t(LOCALE_COMMON_PATHS.SAVE_SEARCH_CARD_DEFAULT_SEARCH_DESCRIPTION, {
    defaultValue: 'My Saved Search',
  });
  const newDescription = listingParams?.filter ? generateSearchDescriptionFromFilters(listingParams.filter) : defaultSearchDescription;
  const isMobile = useIsSmallMobile();
  const { themeName } = useThemeContext();
  const isZoocasaTenant = themeName === ThemeNames.ZOOCASA;

  const saveSearch = async (values: Record<string, unknown>) => {
    createSavedSearch({
      frequency: 1,
      description: values.description as string || defaultSearchDescription,
      formParams: listingParams,
      source: themeName,
    })
      .then(savedSearch => {
        setSavedAttributes(savedSearch);
        setIsSaved(true);
        openModal('notification', { 
          success: t(LOCALE_COMMON_PATHS.SAVE_SEARCH_CARD_NOTIFICATION_SUCCESS_MESSAGE, {
            defaultValue: 'Your search has been saved to your account.',
          }),
        });
      })
      .catch(() => {
        setError(t(LOCALE_COMMON_PATHS.SAVE_SEARCH_CARD_NOTIFICATION_ERROR_MESSAGE, {
          defaultValue: 'Unexpected Error! Your search was not saved.',
        }));
        console.log(error);
      });
  };

  const handleOnClick = () => {
    if (isAuthenticated) {
      saveSearch({ description: newDescription });
      trackEvent(GTM_CLICK_AREA_CARD_SAVE_SEARCH_BUTTON);
    } else {
      openModal('login-registration', { callback: () => {
        saveSearch({ description: newDescription });
        trackEvent(GTM_CLICK_AREA_CARD_EDIT_SEARCH_BUTTON);
      },
      });
    }
  };

  return (
    isSaved ?
      <EditSearchCard listingParams={listingParams} savedAttributes={savedAttributes} isMobile={isMobile} isZoocasaTenant={isZoocasaTenant} isCompact={isCompact} />
      : (
        <div className={buildClassName(styles['card-wrapper'], isMobile && styles['mobile-card-wrapper'], isCompact && styles['compact'] )}>
          <div className={styles['house-image-wrapper']}>
            <NextImage
              src={isZoocasaTenant ? '/next/assets/images/empty-saved-search-zoo.svg' : '/next/assets/images/empty-saved-search-exp.svg'}
              alt={t(LOCALE_COMMON_PATHS.SAVE_SEARCH_CARD_CREATE_ALERT_ALT_TEXT, {
                defaultValue: 'Search within an area',
              })}
              fill={true}
              unoptimized
              style={{ objectFit:'contain' }}
            />
          </div>
          <h2 className={buildClassName(styles.heading, !isZoocasaTenant && styles['exp-theme'])}>
            {t(LOCALE_COMMON_PATHS.SAVE_SEARCH_CARD_CREATE_ALERT_HEADING, {
              defaultValue: 'Create a New Listing Alert',
            })}
          </h2>
          <p>
            {t(LOCALE_COMMON_PATHS.SAVE_SEARCH_CARD_CREATE_ALERT_DESCRIPTION, {
              defaultValue: 'We\'ll keep you up to date on all the new listings for this search',
            })}
          </p>
          <Button
            theme={isZoocasaTenant ? 'primary' : 'secondary'}
            label={t(LOCALE_COMMON_PATHS.SAVE_SEARCH_CARD_CREATE_ALERT_BUTTON, {
              defaultValue: 'Create Alert',
            })}
            onClick={handleOnClick}
          />
        </div>
      )
  );
}
